var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as Sentry from '@sentry/react';
import Button from 'components/Button/Button';
import PopupAlertManager from 'components/PopupAlert/PopupAlertManager';
import _t from 'counterpart';
import { useCallback, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { deleteSubscription, getSubscription, getVapidKey, pushSubscribe } from 'wintrado-api/src/services/crmService';
import { canUsePushNotifications, capitalize, extractErrorMessage, urlBase64ToUint8Array, withTimeout } from '../../../helpers';
const PushToggle = () => {
    const [pushEnabled, setPushEnabled] = useState(false);
    const [loading, setIsLoading] = useState();
    const [permissionState, setPermissionState] = useState('default');
    const pushSupport = canUsePushNotifications();
    const showErrorAlert = useCallback((msg) => {
        PopupAlertManager.showAlert({
            type: 'error',
            title: _t.translate('error.oops'),
            subtitle: msg,
            buttonLabel: _t.translate('main.go_back'),
        });
    }, []);
    const pushSubcribeMutation = useMutation({
        mutationFn: (subsciption) => pushSubscribe(subsciption, 'trading'),
        retry: false,
        onSuccess: () => {
            checkPushEnabledAndSet();
            setIsLoading(false);
            setPushEnabled(true);
        },
        onError: (error) => {
            showErrorAlert(extractErrorMessage(error));
            setIsLoading(false);
            setPushEnabled(false);
        },
    });
    const checkPushEnabledAndSet = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if ('Notification' in window) {
            setPermissionState(Notification.permission);
        }
        if ('serviceWorker' in navigator && 'Notification' in window) {
            try {
                const existingRegistration = yield navigator.serviceWorker.getRegistration();
                if (!existingRegistration) {
                    setPushEnabled(false);
                    return;
                }
                const registration = yield navigator.serviceWorker.ready;
                const subscription = yield registration.pushManager.getSubscription();
                if (!subscription) {
                    setPushEnabled(false);
                    return;
                }
                const dbSubscription = yield getSubscription(subscription.endpoint, 'trading');
                if (!dbSubscription) {
                    setPushEnabled(false);
                    return;
                }
                setPushEnabled(true);
            }
            catch (e) {
                const er = extractErrorMessage(e);
                showErrorAlert(er);
            }
        }
    }), [showErrorAlert]);
    const registerPush = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!pushSupport.supported) {
            setPushEnabled(false);
            return;
        }
        if ('serviceWorker' in navigator && 'Notification' in window) {
            try {
                setIsLoading(true);
                const existingRegistration = yield navigator.serviceWorker.getRegistration();
                if (!existingRegistration) {
                    showErrorAlert(_t.translate('settings.push-service-worker-not-registered'));
                    setPushEnabled(false);
                    setIsLoading(false);
                    return;
                }
                if (Notification.permission === 'denied') {
                    showErrorAlert(_t.translate('settings.push-permission-denied'));
                    setPushEnabled(false);
                    setIsLoading(false);
                    return;
                }
                if (Notification.permission === 'default') {
                    const permission = yield Notification.requestPermission();
                    setPermissionState(permission);
                    if (permission === 'denied') {
                        showErrorAlert(_t.translate('settings.push-permission-denied'));
                        setPushEnabled(false);
                        setIsLoading(false);
                        return;
                    }
                }
                const registration = yield withTimeout(navigator.serviceWorker.ready, 10000, 'Service worker ready timeout');
                const subscription = yield withTimeout(registration.pushManager.getSubscription(), 5000, 'Get subscription timeout');
                if (subscription) {
                    yield deleteSubscription(subscription.endpoint, 'trading');
                    yield subscription.unsubscribe();
                }
                const vapidKey = yield withTimeout(getVapidKey(), 10000, 'Get VAPID key timeout');
                const convertedVapidKey = urlBase64ToUint8Array(vapidKey);
                const pushSubscription = yield withTimeout(registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: convertedVapidKey,
                }), 15000, 'Push subscription timeout');
                yield pushSubcribeMutation.mutateAsync(pushSubscription);
            }
            catch (e) {
                Sentry.captureException(e);
                const errorMsg = extractErrorMessage(e);
                const isTimeout = errorMsg.includes('timeout');
                showErrorAlert(isTimeout ? _t.translate('error.try_again_later') : errorMsg);
                setPushEnabled(false);
            }
            finally {
                setIsLoading(false);
            }
        }
    });
    const disablePush = () => __awaiter(void 0, void 0, void 0, function* () {
        if ('serviceWorker' in navigator && 'Notification' in window) {
            try {
                setIsLoading(true);
                const registration = yield navigator.serviceWorker.ready;
                const subscription = yield registration.pushManager.getSubscription();
                if (subscription) {
                    yield deleteSubscription(subscription.endpoint, 'trading');
                    yield subscription.unsubscribe();
                }
                checkPushEnabledAndSet();
            }
            catch (e) {
                const er = extractErrorMessage(e);
                showErrorAlert(er);
            }
            finally {
                setIsLoading(false);
            }
        }
    });
    useEffect(() => {
        checkPushEnabledAndSet();
    }, [checkPushEnabledAndSet]);
    const togglePush = () => {
        if (!pushSupport.supported) {
            if (pushSupport.reason === 'ios_not_pwa') {
                showErrorAlert(_t.translate('settings.push-ios-pwa-required'));
            }
            else {
                showErrorAlert(_t.translate('settings.push-not-supported'));
            }
            return;
        }
        if (permissionState === 'denied') {
            showErrorAlert(_t.translate('settings.push-permission-denied'));
            return;
        }
        if (!pushEnabled) {
            registerPush();
        }
        else {
            disablePush();
        }
    };
    return (_jsx(Button, Object.assign({ gray: true, fullWidth: true, withImage: true, loading: loading, faded: !pushEnabled, textStart: true, onClick: togglePush }, { children: _jsx("span", { children: capitalize(_t(pushEnabled ? 'page.on' : 'page.off')) }) })));
};
export default PushToggle;
